import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError, formatINR } from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, Mail, MapPin, GraduationCap, Calendar, Bot, MessageSquare,
  Sparkles, User, BookOpen, Target, Clock, StickyNote, Save, Plus, Star,
  Flag, UserCog, CalendarClock, Loader2, CheckCircle2, Layers, FileText, PhoneCall, MessageCircle,
} from "lucide-react";

const STATUSES = ["new", "contacted", "interested", "admitted", "lost"];
const STAGES = ["New", "Attempted", "Connected", "Interested", "Documents Pending", "Application Started", "Application Submitted", "Admission Confirmed", "Fees Paid", "Lost"];
const WON = ["Admission Confirmed", "Fees Paid"];
const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-700", contacted: "bg-amber-100 text-amber-700",
  interested: "bg-purple-100 text-purple-700", admitted: "bg-emerald-100 text-emerald-700",
  lost: "bg-slate-200 text-slate-600",
};
const PRIORITY_COLORS = { High: "bg-red-100 text-red-700", Medium: "bg-amber-100 text-amber-700", Low: "bg-slate-200 text-slate-600" };
const SOURCE_LABELS = { ai_counseling: "AI Counseling", expert_counseling: "Expert Counseling", contact_form: "Contact Form", college_page_cta: "College Page CTA" };

const CRM_FIELDS = [
  "gender", "dob", "parentName", "parentMobile", "whatsappNumber", "alternateMobile",
  "currentQualification", "percentage10", "percentage12", "graduationPercentage", "entranceExam", "entranceScore", "passingYear",
  "preferredState", "preferredCity", "preferredCollege", "preferredCourse", "hostelRequired", "scholarshipRequired", "studyMode",
  "leadScore", "priority", "counsellorAssigned", "lastFollowUp", "nextFollowUp", "remarks", "courseInterest",
];

export default function AdminLeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [tlEvent, setTlEvent] = useState("");
  const [docName, setDocName] = useState("");
  const [docUrl, setDocUrl] = useState("");

  const hydrate = (d) => {
    setLead(d);
    const e = {};
    CRM_FIELDS.forEach((f) => { e[f] = d[f] ?? (f === "hostelRequired" || f === "scholarshipRequired" ? false : ""); });
    setEdit(e);
  };
  const load = () => api.get(`/admin/leads/${id}`).then((r) => hydrate(r.data)).catch(() => setLead(null));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const set = (k, v) => setEdit((e) => ({ ...e, [k]: v }));

  const saveCrm = async () => {
    setSaving(true);
    try {
      const payload = { ...edit, leadScore: edit.leadScore === "" ? undefined : Number(edit.leadScore) };
      const r = await api.patch(`/admin/leads/${id}`, payload);
      hydrate(r.data);
      toast.success("CRM details saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed."); }
    finally { setSaving(false); }
  };

  const updateStatus = async (status) => {
    try { const r = await api.patch(`/admin/leads/${id}/status`, { status }); hydrate(r.data); toast.success(`Status: ${status}`); }
    catch (e) { toast.error("Update failed."); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try { const r = await api.post(`/admin/leads/${id}/notes`, { text: noteText.trim() }); hydrate(r.data); setNoteText(""); toast.success("Note added"); }
    catch (e) { toast.error("Could not add note."); }
  };

  const addTimeline = async () => {
    if (!tlEvent.trim()) return;
    try { const r = await api.post(`/admin/leads/${id}/timeline`, { event: tlEvent.trim() }); hydrate(r.data); setTlEvent(""); toast.success("Timeline updated"); }
    catch (e) { toast.error("Could not add event."); }
  };

  const addDoc = async () => {
    if (!docName.trim()) return;
    try { const r = await api.post(`/admin/leads/${id}/documents`, { name: docName.trim(), url: docUrl.trim() }); hydrate(r.data); setDocName(""); setDocUrl(""); toast.success("Document added"); }
    catch (e) { toast.error("Could not add document."); }
  };

  const setStage = async (stage) => {
    try { const r = await api.patch(`/admin/leads/${id}`, { stage }); hydrate(r.data); toast.success(`Stage: ${stage}`); }
    catch (e) { toast.error("Could not update stage."); }
  };

  if (!lead) return <div className="flex justify-center py-32"><Loader2 className="h-10 w-10 animate-spin text-[#0B2A6B]" /></div>;

  return (
    <div data-testid="admin-lead-detail-page" className="space-y-6">
      <SEOHead title="Lead CRM" />
      <Link to="/admin/leads" className="inline-flex items-center gap-1 text-sm font-semibold text-[#0B2A6B] hover:underline"><ArrowLeft className="h-4 w-4" /> Back to leads</Link>

      {/* Header */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0B2A6B] text-lg font-bold text-white">{(lead.name || "?")[0]?.toUpperCase()}</span>
          <div>
            <h1 className="font-display text-xl font-bold text-slate-900">{lead.name}</h1>
            <p className="text-sm text-slate-500">{SOURCE_LABELS[lead.source] || lead.source} · {lead.mobile}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${WON.includes(lead.stage) ? "bg-emerald-100 text-emerald-700" : "bg-indigo-100 text-indigo-700"}`}>{lead.stage || "New"}</span>
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${PRIORITY_COLORS[lead.priority] || PRIORITY_COLORS.Medium}`}>{lead.priority || "Medium"} priority</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-[#FDF9F1] px-3 py-1 text-xs font-semibold text-[#C08E3B]"><Star className="h-3 w-3" /> Score {lead.leadScore ?? 0}</span>
        </div>
      </div>

      {/* Quick contact actions */}
      <div className="flex flex-wrap gap-2">
        <a data-testid="action-whatsapp" href={`https://wa.me/91${(lead.whatsappNumber || lead.mobile || "").replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent("Hi " + (lead.name || "") + ", this is CollegeUnlock admission team.")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
        <a data-testid="action-call" href={`tel:${lead.mobile}`} className="inline-flex items-center gap-2 rounded-lg bg-[#0B2A6B] px-4 py-2 text-sm font-semibold text-white hover:bg-[#081E4D]"><PhoneCall className="h-4 w-4" /> Call</a>
        <a data-testid="action-email" href={`mailto:${lead.email || ""}?subject=${encodeURIComponent("CollegeUnlock Admission Guidance")}`} className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold ${lead.email ? "border-slate-300 text-slate-700 hover:bg-slate-50" : "border-slate-200 text-slate-300 pointer-events-none"}`}><Mail className="h-4 w-4" /> Email</a>
      </div>

      {/* Admission Pipeline stepper */}
      <div data-testid="stage-pipeline" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 font-display font-bold text-slate-900"><Layers className="h-5 w-5 text-[#0B2A6B]" /> Admission Pipeline</h3>
        <div className="flex flex-wrap gap-2">
          {STAGES.map((s) => {
            const active = (lead.stage || "New") === s;
            const isLost = s === "Lost";
            return (
              <button
                key={s}
                data-testid={`stage-btn-${s.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => setStage(s)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                  active
                    ? isLost ? "bg-slate-600 text-white" : WON.includes(s) ? "bg-emerald-600 text-white" : "bg-[#0B2A6B] text-white"
                    : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {s}
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-slate-400">Current stage: <span className="font-semibold text-slate-600">{lead.stage || "New"}</span> · Click a stage to advance the lead. Stage changes are logged to the timeline.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* LEFT */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact + core (read-only) */}
          <Panel icon={User} title="Student Profile">
            <Grid>
              <RO label="Mobile" value={lead.mobile} />
              {lead.email && <RO label="Email" value={lead.email} />}
              <RO label="City" value={lead.city} />
              <Inp label="Gender" type="select" options={["", "Male", "Female", "Other"]} value={edit.gender} onChange={(v) => set("gender", v)} testid="f-gender" />
              <Inp label="Date of Birth" type="date" value={edit.dob} onChange={(v) => set("dob", v)} testid="f-dob" />
              <Inp label="Parent Name" value={edit.parentName} onChange={(v) => set("parentName", v)} testid="f-parentName" />
              <Inp label="Parent Mobile" value={edit.parentMobile} onChange={(v) => set("parentMobile", v)} testid="f-parentMobile" />
              <Inp label="WhatsApp Number" value={edit.whatsappNumber} onChange={(v) => set("whatsappNumber", v)} testid="f-whatsapp" />
              <Inp label="Alternate Mobile" value={edit.alternateMobile} onChange={(v) => set("alternateMobile", v)} testid="f-altMobile" />
            </Grid>
          </Panel>

          <Panel icon={BookOpen} title="Academic Information">
            <Grid>
              <Inp label="Current Qualification" value={edit.currentQualification} onChange={(v) => set("currentQualification", v)} testid="f-qual" />
              <Inp label="10th %" value={edit.percentage10} onChange={(v) => set("percentage10", v)} testid="f-p10" />
              <Inp label="12th %" value={edit.percentage12} onChange={(v) => set("percentage12", v)} testid="f-p12" />
              <Inp label="Graduation %" value={edit.graduationPercentage} onChange={(v) => set("graduationPercentage", v)} testid="f-pgrad" />
              <Inp label="Entrance Exam" value={edit.entranceExam} onChange={(v) => set("entranceExam", v)} testid="f-exam" />
              <Inp label="Entrance Score" value={edit.entranceScore} onChange={(v) => set("entranceScore", v)} testid="f-score" />
              <Inp label="Passing Year" value={edit.passingYear} onChange={(v) => set("passingYear", v)} testid="f-year" />
            </Grid>
          </Panel>

          <Panel icon={Target} title="Admission Preferences">
            <Grid>
              <Inp label="Course Interest" value={edit.courseInterest} onChange={(v) => set("courseInterest", v)} testid="f-course" />
              <Inp label="Preferred Course" value={edit.preferredCourse} onChange={(v) => set("preferredCourse", v)} testid="f-prefCourse" />
              <Inp label="Preferred State" value={edit.preferredState} onChange={(v) => set("preferredState", v)} testid="f-state" />
              <Inp label="Preferred City" value={edit.preferredCity} onChange={(v) => set("preferredCity", v)} testid="f-prefCity" />
              <Inp label="Preferred College" value={edit.preferredCollege} onChange={(v) => set("preferredCollege", v)} testid="f-prefCollege" />
              <Inp label="Study Mode" type="select" options={["", "Classroom", "Online", "Hybrid"]} value={edit.studyMode} onChange={(v) => set("studyMode", v)} testid="f-mode" />
              <Toggle label="Hostel Required" value={edit.hostelRequired} onChange={(v) => set("hostelRequired", v)} testid="f-hostel" />
              <Toggle label="Scholarship Required" value={edit.scholarshipRequired} onChange={(v) => set("scholarshipRequired", v)} testid="f-scholarship" />
            </Grid>
          </Panel>

          {/* AI summary (preserved) */}
          {lead.aiSummary && (
            <Panel icon={Bot} title="AI Counselling Summary" iconColor="#D4A24C">
              <p className="text-sm text-slate-600 leading-relaxed">{lead.aiSummary}</p>
              {lead.recommendedColleges?.length > 0 && (
                <div className="mt-3 space-y-2">
                  {lead.recommendedColleges.map((c) => (
                    <Link key={c.id} to={`/colleges/${c.slug}`} className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100">
                      <Sparkles className="h-3.5 w-3.5 text-[#D4A24C]" /> {c.name} <span className="text-xs text-slate-400">· NAAC {c.naacGrade}</span>
                    </Link>
                  ))}
                </div>
              )}
            </Panel>
          )}
          {(lead.careerGoal || lead.message) && (
            <Panel icon={MessageSquare} title="Enquiry Details">
              {lead.careerGoal && <div className="mb-2"><p className="text-xs uppercase text-slate-400">Career Goal</p><p className="text-sm text-slate-700">{lead.careerGoal}</p></div>}
              {lead.message && <div><p className="text-xs uppercase text-slate-400">Message</p><p className="text-sm text-slate-700">{lead.message}</p></div>}
              {lead.budget && <p className="mt-2 text-sm text-slate-600">Budget: ₹{formatINR(lead.budget)}/yr</p>}
            </Panel>
          )}

          {/* Notes */}
          <Panel icon={StickyNote} title="Internal Notes">
            <div className="flex gap-2">
              <input data-testid="note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add an internal note…" className="ci flex-1" onKeyDown={(e) => e.key === "Enter" && addNote()} />
              <button data-testid="add-note-btn" onClick={addNote} className="inline-flex items-center gap-1 rounded-lg bg-[#0B2A6B] px-4 text-sm font-semibold text-white hover:bg-[#081E4D]"><Plus className="h-4 w-4" /></button>
            </div>
            <div className="mt-4 space-y-2">
              {(lead.internalNotes || []).length === 0 && <p className="text-sm text-slate-400">No notes yet.</p>}
              {(lead.internalNotes || []).slice().reverse().map((n, i) => (
                <div key={`${n.at}-${i}`} className="rounded-lg bg-slate-50 p-3 text-sm">
                  <p className="text-slate-700">{n.text}</p>
                  <p className="mt-1 text-xs text-slate-400">{n.by} · {fmt(n.at)}</p>
                </div>
              ))}
            </div>
          </Panel>

          {/* Enquiry Context (Sprint 1 lead intelligence) */}
          {(lead.collegeName || lead.attribution || lead.admissionYear || lead.budget) && (
            <Panel icon={Sparkles} title="Enquiry Context">
              <dl className="grid sm:grid-cols-2 gap-3 text-sm">
                {lead.collegeName && <CtxField label="Interested College" value={lead.collegeName} />}
                {lead.courseInterest && <CtxField label="Interested Course" value={lead.courseInterest} />}
                {lead.admissionYear && <CtxField label="Admission Year" value={lead.admissionYear} />}
                {lead.budget && <CtxField label="Budget Range" value={lead.budget} />}
                {(lead.city || lead.state) && <CtxField label="Location" value={[lead.city, lead.state].filter(Boolean).join(", ")} />}
                {lead.intent && <CtxField label="Intent" value={lead.intent} />}
                {lead.attribution?.trafficSource && <CtxField label="Traffic Source" value={lead.attribution.trafficSource} />}
                {lead.attribution?.landingPage && <CtxField label="Landing Page" value={lead.attribution.landingPage} />}
                {lead.attribution?.searchQuery && <CtxField label="Search Query" value={lead.attribution.searchQuery} />}
                {lead.attribution?.device && <CtxField label="Device" value={lead.attribution.device} />}
                {lead.attribution?.utmCampaign && <CtxField label="UTM Campaign" value={lead.attribution.utmCampaign} />}
              </dl>
            </Panel>
          )}

          {/* Documents */}
          <Panel icon={FileText} title="Documents">
            <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2">
              <input data-testid="doc-name-input" value={docName} onChange={(e) => setDocName(e.target.value)} placeholder="Document name (e.g. 12th Marksheet)" className="ci" />
              <input data-testid="doc-url-input" value={docUrl} onChange={(e) => setDocUrl(e.target.value)} placeholder="Link / URL (optional)" className="ci" onKeyDown={(e) => e.key === "Enter" && addDoc()} />
              <button data-testid="add-doc-btn" onClick={addDoc} className="inline-flex items-center justify-center gap-1 rounded-lg bg-[#0B2A6B] px-4 text-sm font-semibold text-white hover:bg-[#081E4D]"><Plus className="h-4 w-4" /> Add</button>
            </div>
            <div className="mt-4 space-y-2">
              {(lead.documents || []).length === 0 && <p className="text-sm text-slate-400">No documents tracked yet.</p>}
              {(lead.documents || []).slice().reverse().map((d, i) => (
                <div key={`${d.at}-${i}`} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 p-3 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 shrink-0 text-[#0B2A6B]" />
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 truncate">{d.name}</p>
                      <p className="text-xs text-slate-400">{fmt(d.at)}</p>
                    </div>
                  </div>
                  {d.url ? (
                    <a href={d.url} target="_blank" rel="noreferrer" className="shrink-0 text-xs font-semibold text-[#0B2A6B] hover:underline">Open</a>
                  ) : <span className="shrink-0 text-xs text-slate-300">No link</span>}
                </div>
              ))}
            </div>
          </Panel>
        </div>

        {/* RIGHT */}
        <aside className="space-y-6">
          {/* CRM management */}
          <Panel icon={UserCog} title="CRM Management" sticky>
            <div className="space-y-3">
              <Inp label="Priority" type="select" options={["High", "Medium", "Low"]} value={edit.priority || "Medium"} onChange={(v) => set("priority", v)} testid="f-priority" />
              <Inp label="Lead Score (0-100)" type="number" value={edit.leadScore} onChange={(v) => set("leadScore", v)} testid="f-leadScore" />
              <Inp label="Counsellor Assigned" value={edit.counsellorAssigned} onChange={(v) => set("counsellorAssigned", v)} testid="f-counsellor" />
              <Inp label="Last Follow-up" type="date" value={edit.lastFollowUp} onChange={(v) => set("lastFollowUp", v)} testid="f-lastFollow" />
              <Inp label="Next Follow-up" type="date" value={edit.nextFollowUp} onChange={(v) => set("nextFollowUp", v)} testid="f-nextFollow" />
              <Inp label="Remarks" type="textarea" value={edit.remarks} onChange={(v) => set("remarks", v)} testid="f-remarks" />
              <button data-testid="save-crm-btn" onClick={saveCrm} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#0B2A6B] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#081E4D] disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save CRM Details
              </button>
            </div>
          </Panel>

          {/* Status pipeline */}
          <Panel icon={Flag} title="Status">
            <div className="grid grid-cols-2 gap-2">
              {STATUSES.map((s) => (
                <button key={s} data-testid={`status-btn-${s}`} onClick={() => updateStatus(s)}
                  className={`rounded-lg px-3 py-2 text-sm font-semibold capitalize ${lead.status === s ? "bg-[#0B2A6B] text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{s}</button>
              ))}
            </div>
          </Panel>

          {/* Timeline */}
          <Panel icon={Clock} title="Lead Timeline">
            <div className="flex gap-2 mb-4">
              <input data-testid="timeline-input" value={tlEvent} onChange={(e) => setTlEvent(e.target.value)} placeholder="Add event (e.g. Application Submitted)" className="ci flex-1" onKeyDown={(e) => e.key === "Enter" && addTimeline()} />
              <button data-testid="add-timeline-btn" onClick={addTimeline} className="inline-flex items-center rounded-lg bg-[#D4A24C] px-3 text-sm font-semibold text-white hover:bg-[#C08E3B]"><Plus className="h-4 w-4" /></button>
            </div>
            <ol className="relative border-l-2 border-slate-100 ml-2 space-y-4">
              {(lead.timeline || []).map((t, i) => (
                <li key={`${t.at}-${i}`} className="ml-4">
                  <span className="absolute -left-[7px] flex h-3 w-3 rounded-full bg-[#0B2A6B]" />
                  <p className="text-sm font-semibold text-slate-800">{t.event}</p>
                  {t.detail && <p className="text-xs text-slate-500">{t.detail}</p>}
                  <p className="text-xs text-slate-400">{fmt(t.at)}</p>
                </li>
              ))}
            </ol>
          </Panel>
        </aside>
      </div>
      <style>{`.ci{display:block;height:2.5rem;width:100%;border-radius:0.5rem;border:1px solid #cbd5e1;background:#fff;padding:0 0.75rem;font-size:0.875rem;outline:none}.ci:focus{box-shadow:0 0 0 2px #0B2A6B;border-color:transparent}`}</style>
    </div>
  );
}

function fmt(iso) { try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } }

function Panel({ icon: Icon, title, children, iconColor = "#0B2A6B", sticky }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${sticky ? "lg:sticky lg:top-6" : ""}`}>
      <h3 className="mb-4 flex items-center gap-2 font-display font-bold text-slate-900"><Icon className="h-5 w-5" style={{ color: iconColor }} /> {title}</h3>
      {children}
    </div>
  );
}
function Grid({ children }) { return <div className="grid sm:grid-cols-2 gap-3">{children}</div>; }
function RO({ label, value }) {
  return <div><p className="text-xs uppercase text-slate-400">{label}</p><p className="text-sm font-medium text-slate-900">{value || "—"}</p></div>;
}
function Inp({ label, value, onChange, type = "text", options, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase text-slate-400">{label}</span>
      {type === "select" ? (
        <select data-testid={testid} className="ci mt-1" value={value || ""} onChange={(e) => onChange(e.target.value)}>
          {options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
        </select>
      ) : type === "textarea" ? (
        <textarea data-testid={testid} rows={2} className="ci mt-1 !h-auto py-2" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input data-testid={testid} type={type} className="ci mt-1" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      )}
    </label>
  );
}
function Toggle({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 mt-5 text-sm text-slate-700">
      <input data-testid={testid} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} /> {label}
    </label>
  );
}

function CtxField({ label, value }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-slate-800 break-words">{value || "—"}</dd>
    </div>
  );
}
